/**
 * Enhanced permissions hook that integrates v2 permission system
 * Maintains backward compatibility with existing permission checks
 */

import { useMemo, useCallback } from 'react';
import { useAuth } from './core/useAuth';
import { PERMISSIONS, ROLE_PERMISSIONS, ROLES_HIERARCHY } from '../config/permissions';
import PermissionEngine from '../services/PermissionEngine';
import { SCOPES } from '../config/permissions-v2';

export const usePermissionsV2 = () => {
  const { user, staffProfile } = useAuth();
  const userRole = staffProfile?.role || 'staff';
  
  // Create permission engine instance
  const permissionEngine = useMemo(() => {
    if (!staffProfile) return null;
    return new PermissionEngine(staffProfile);
  }, [staffProfile]);
  
  /**
   * Legacy permission check (backward compatibility)
   */
  const has = useCallback((permission) => {
    const rolePermissions = ROLE_PERMISSIONS[userRole] || [];
    return rolePermissions.includes(permission);
  }, [userRole]);
  
  /**
   * New permission check with v2 system
   */
  const can = useCallback((permission, options = {}) => {
    if (!permissionEngine) return { allowed: false, reason: 'Not authenticated' };
    return permissionEngine.can(permission, options);
  }, [permissionEngine]);
  
  /**
   * Check if user can perform action on resource
   */
  const canDo = useCallback((action, module, resource, options = {}) => {
    const permission = `${module}.${resource}.${action}`;
    return can(permission, options);
  }, [can]);
  
  /**
   * Check multiple permissions (OR)
   */
  const canAny = useCallback((permissions, options = {}) => {
    if (!permissionEngine) return false;
    return permissionEngine.canAny(permissions, options);
  }, [permissionEngine]);
  
  /**
   * Check multiple permissions (AND)
   */
  const canAll = useCallback((permissions, options = {}) => {
    if (!permissionEngine) return false;
    return permissionEngine.canAll(permissions, options);
  }, [permissionEngine]);
  
  /**
   * Get user's scope for a permission
   */
  const getScope = useCallback((permission) => {
    if (!permissionEngine) return null;
    const result = permissionEngine.can(permission);
    return result.allowed ? result.scope : null;
  }, [permissionEngine]);
  
  /**
   * Check if user has access to specific fields
   */
  const canAccessFields = useCallback((permission, fields) => {
    if (!permissionEngine) return [];
    const result = permissionEngine.can(permission, { fields });
    return result.allowed ? result.fields || [] : [];
  }, [permissionEngine]);
  
  /**
   * Get all permissions for the user
   */
  const getAllPermissions = useCallback(() => {
    if (!permissionEngine) return [];
    return permissionEngine.getAllPermissions();
  }, [permissionEngine]);
  
  // Legacy permission helpers (backward compatibility)
  const canView = useCallback(() => has(PERMISSIONS.STAFF_VIEW), [has]);
  const canCreateStaff = useCallback(() => has(PERMISSIONS.STAFF_CREATE), [has]);
  const canInviteStaff = useCallback(() => has(PERMISSIONS.STAFF_INVITE), [has]);
  
  const canEdit = useCallback((targetUser) => {
    if (user?.uid === targetUser?.id) return true;
    return has(PERMISSIONS.STAFF_EDIT) && canActOn(targetUser);
  }, [has, user]);
  
  const canDelete = useCallback((targetUser) => {
    if (user?.uid === targetUser?.id) return false;
    return has(PERMISSIONS.STAFF_DELETE) && canActOn(targetUser);
  }, [has, user]);
  
  const canSuspend = useCallback((targetUser) => {
    if (user?.uid === targetUser?.id) return false;
    return has(PERMISSIONS.STAFF_SUSPEND) && canActOn(targetUser);
  }, [has, user]);
  
  const canResetPassword = useCallback((targetUser) => {
    if (user?.uid === targetUser?.id) return false;
    return has(PERMISSIONS.STAFF_RESET_PASSWORD) && canActOn(targetUser);
  }, [has, user]);
  
  const canChangeUserRole = useCallback((targetUser) => {
    if (user?.uid === targetUser?.id) return false;
    return has(PERMISSIONS.STAFF_CHANGE_ROLE) && canActOn(targetUser);
  }, [has, user]);
  
  const canActOn = useCallback((targetUser) => {
    const userLevel = ROLES_HIERARCHY[userRole] || 0;
    const targetLevel = ROLES_HIERARCHY[targetUser?.role] || 0;
    return userLevel > targetLevel;
  }, [userRole]);
  
  const isManager = useCallback(() => {
    const userLevel = ROLES_HIERARCHY[userRole] || 0;
    return userLevel >= ROLES_HIERARCHY.manager;
  }, [userRole]);
  
  const isAdmin = useCallback(() => userRole === 'admin', [userRole]);
  
  // Department permissions (legacy)
  const canCreateDepartment = useCallback(() => has(PERMISSIONS.DEPARTMENT_CREATE), [has]);
  const canEditDepartment = useCallback(() => has(PERMISSIONS.DEPARTMENT_EDIT), [has]);
  const canDeleteDepartment = useCallback(() => has(PERMISSIONS.DEPARTMENT_DELETE), [has]);
  const canViewDepartments = useCallback(() => has(PERMISSIONS.DEPARTMENT_VIEW), [has]);
  
  // Job title permissions (legacy)
  const canCreateJobTitle = useCallback(() => has(PERMISSIONS.JOB_TITLE_CREATE), [has]);
  const canEditJobTitle = useCallback(() => has(PERMISSIONS.JOB_TITLE_EDIT), [has]);
  const canDeleteJobTitle = useCallback(() => has(PERMISSIONS.JOB_TITLE_DELETE), [has]);
  const canViewJobTitles = useCallback(() => has(PERMISSIONS.JOB_TITLE_VIEW), [has]);
  
  /**
   * V2 Permission helpers
   */
  
  // Check if user can access a module
  const canAccessModule = useCallback((module) => {
    // Check if user has any permission in the module
    const modulePermissions = [
      `${module}.*.view`,
      `${module}.*.create`,
      `${module}.*.edit`,
    ];
    return canAny(modulePermissions);
  }, [canAny]);
  
  // Check data access scope
  const canAccessData = useCallback((data, permission) => {
    if (!permissionEngine || !data) return false;
    
    const result = permissionEngine.can(permission);
    if (!result.allowed) return false;
    
    const scope = result.scope || SCOPES.OWN;
    
    switch (scope) {
      case SCOPES.OWN:
        return data.ownerId === user?.uid || data.createdBy === user?.uid;
        
      case SCOPES.TEAM:
        return staffProfile?.teams?.includes(data.team) || 
               data.ownerId === user?.uid;
        
      case SCOPES.DEPARTMENT:
        return staffProfile?.department === data.department ||
               staffProfile?.departments?.includes(data.department);
        
      case SCOPES.LOCATION:
        return staffProfile?.location === data.location ||
               staffProfile?.locations?.includes(data.location);
        
      case SCOPES.ALL:
        return true;
        
      default:
        return false;
    }
  }, [permissionEngine, user, staffProfile]);
  
  // Check if user is in a permission group
  const hasPermissionGroup = useCallback((groupId) => {
    return staffProfile?.permissions?.groups?.includes(groupId) || false;
  }, [staffProfile]);
  
  // Get user's permission groups
  const getPermissionGroups = useCallback(() => {
    return staffProfile?.permissions?.groups || [];
  }, [staffProfile]);
  
  // Clear permission cache (useful after permission updates)
  const clearCache = useCallback(() => {
    if (permissionEngine) {
      permissionEngine.clearCache();
    }
  }, [permissionEngine]);
  
  return {
    // Legacy methods (backward compatibility)
    has,
    canView,
    canCreateStaff,
    canEdit,
    canDelete,
    canSuspend,
    canInviteStaff,
    canResetPassword,
    canChangeUserRole,
    canActOn,
    isManager,
    isAdmin,
    canCreateDepartment,
    canEditDepartment,
    canDeleteDepartment,
    canViewDepartments,
    canCreateJobTitle,
    canEditJobTitle,
    canDeleteJobTitle,
    canViewJobTitles,
    
    // V2 methods
    can,
    canDo,
    canAny,
    canAll,
    getScope,
    canAccessFields,
    getAllPermissions,
    canAccessModule,
    canAccessData,
    hasPermissionGroup,
    getPermissionGroups,
    clearCache,
    
    // User info
    user,
    userRole,
    staffProfile,
  };
};

// HOC for protecting components with permissions
export const withPermission = (Component, permission, options = {}) => {
  return (props) => {
    const permissions = usePermissionsV2();
    const result = permissions.can(permission, options);
    
    if (!result.allowed) {
      return options.fallback || null;
    }
    
    return <Component {...props} permissionResult={result} />;
  };
};

// Hook for checking permissions in effects
export const usePermissionCheck = (permission, options = {}, deps = []) => {
  const permissions = usePermissionsV2();
  
  return useMemo(() => {
    return permissions.can(permission, options);
  }, [permissions, permission, ...deps]);
};

export default usePermissionsV2;